import {FlowLang} from 'flow-to-typescript-codemod';
import {isEqual} from 'lodash';
import {getName, qualify, namesEqual} from '@packages/systems/wfdl-Qualified';
import {
  matchExpression,
  Component,
  Plugins,
  Types,
  Expressions,
  type Type,
  type ExpressionPath,
  stringifyExpression,
} from '@packages/systems/wfdl';
import {TEMP_PATH} from '@packages/utilities/expression-path-constants';
import {
  emptyArray,
  emptyObject,
  flip,
  concat,
  constant,
} from '@packages/utilities/fp/utils';
import {dissoc} from '@packages/utilities/key-map';
import {weakMemo, cacheMemo} from '@packages/utilities/memo';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {getElementDataAndBindForJSONNode} from '@packages/systems/core/component-conversions';
import {
  getChildren,
  isUserComponentInst,
  isComponentInst,
  isUserComponent,
  CHILDREN_PATH,
  isUserComponentRoot,
  reduceElementTree,
  getTag,
} from '@packages/utilities/platform';
import {getStyleBlockIds} from '@packages/systems/style/style-blocks';

import {isStringElement, isBody} from '@packages/utilities/elementTypeUtils';
import {
  EElement,
  Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  defaultIntegratedTypes,
  devlinkDataType,
  type IntegratedTypesType,
} from '@packages/utilities/integrated-types';
import {generateGuid} from '@packages/utilities/guid';
import {pair} from '@packages/utilities/Pair';

const createTextJSONNodeFromElement = (
  element: ExpressionElement
): JSONNode => {
  return {
    _id: element.id,
    text: true,
    v: Expressions.getElementDataAt(element, [], 'Text', undefined),
    ...(element.meta && {meta: element.meta}),
  };
};

const pathsToOmit: Array<ExpressionPath> = [
  TEMP_PATH, // data.temp
  [{in: 'Record', at: 'styleBlockIds'}], // data.styleBlockids
];
const pathsToOmitWithChildren: Array<ExpressionPath> = [
  ...pathsToOmit,
  [{in: 'Record', at: 'children'}],
];

const isPlainObject = (data: unknown): boolean => {
  return data !== null && typeof data === 'object' && !Array.isArray(data);
};

/**
 * compress takes a nodeData and compares it against defaultData.
 * If nodeData and defaultData both contain a field of the same value,
 * that data is deleted in nodeData, unless atomNodeData contains a
 * field of a different value.
 *
 * note: compress is function that mutates the nodeData parameter.
 * we opted to use mutation for efficiency
 */
export const compress = (
  nodeData: {
    [key: string]: any;
  },
  defaultData: Readonly<{
    [key: string]: any;
  }>,
  atomDefaultData: null | Readonly<{
    [key: string]: any;
  }>
) => {
  Object.keys(defaultData).forEach((key) => {
    if (!(key in nodeData)) return;
    if (
      isEqual(nodeData[key], defaultData[key]) &&
      (atomDefaultData === null ||
        isEqual(defaultData[key], atomDefaultData[key]))
    ) {
      // eslint-disable-next-line webflow/no-delete
      delete nodeData[key];
    } else if (
      isPlainObject(nodeData[key]) &&
      isPlainObject(defaultData[key])
    ) {
      compress(
        nodeData[key],
        defaultData[key],
        atomDefaultData !== null && isPlainObject(atomDefaultData[key])
          ? atomDefaultData[key]
          : null
      );

      // if nodeData[key] is an empty object after compression, remove it from nodeData
      if (Object.keys(nodeData[key]).length === 0) {
        // eslint-disable-next-line webflow/no-delete
        delete nodeData[key];
      }
    }
  });
};

type IntegratedTypesWithDevlinkType = IntegratedTypesType & {
  devlink: Type;
};

type WeakDefaultValueType = Readonly<{
  [key: string]: any;
}>;

type IntegratedTypeDefaultValues = FlowLang.ObjMap<
  IntegratedTypesWithDevlinkType,
  () => WeakDefaultValueType
>;
type AtomIntegratedTypeDefaultValues = Partial<IntegratedTypeDefaultValues>;

const DEFAULT_INTEGRATED_TYPES: Readonly<IntegratedTypesWithDevlinkType> = {
  ...defaultIntegratedTypes,
  devlink: devlinkDataType,
};

function getIntegratedTypeDefaultValues(
  plugins: Plugins.PluginsType
): Readonly<IntegratedTypeDefaultValues> {
  const getDefaultValue = Types.getTypeDefaultValue(plugins);
  return {
    displayName: getDefaultValue(DEFAULT_INTEGRATED_TYPES.displayName),
    attr: getDefaultValue(DEFAULT_INTEGRATED_TYPES.attr),
    xattr: getDefaultValue(DEFAULT_INTEGRATED_TYPES.xattr),
    styleBlockIds: getDefaultValue(DEFAULT_INTEGRATED_TYPES.styleBlockIds),
    search: getDefaultValue(DEFAULT_INTEGRATED_TYPES.search),
    visibility: getDefaultValue(DEFAULT_INTEGRATED_TYPES.visibility),
    devlink: getDefaultValue(DEFAULT_INTEGRATED_TYPES.devlink),
  };
}

const byNodeTypeMemo = cacheMemo(100);

const getAtomIntegratedTypeDefaultValues = weakMemo(
  (plugins: Plugins.PluginsType) => {
    // JSON nodes do not include plugin name information,
    // so we need to use the deprecated atom name mapping.
    const atoms = Plugins.__DEPRECATED__getAtoms(plugins);

    return byNodeTypeMemo(
      (nodeType: string): Readonly<AtomIntegratedTypeDefaultValues> | null => {
        const atomConfig = nodeType ? atoms[nodeType] : null;

        if (!atomConfig) {
          return null;
        }

        const fields = getAtomDataTypeFields(atomConfig.dataType, plugins);

        if (!fields) {
          return null;
        }

        const atomDefaults: AtomIntegratedTypeDefaultValues = {};

        // extract default values for the fields in the atom data type
        // that overlap with the default integrated types (and devlink)
        for (const key in DEFAULT_INTEGRATED_TYPES) {
          const type = fields[key];
          if (type) {
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Partial<ObjMap<IntegratedTypesWithDevlinkType, () => Readonly<{ [key: string]: any; }>>>'.
            atomDefaults[key] = Types.getTypeDefaultValue(plugins)(type);
          }
        }

        return atomDefaults;
      }
    );
  }
);

/**
 * Given a Type, returns the record fields of that Type.
 *
 * If Type is an alias, we recursively unwind the aliases
 * until we find a non-alias Type that we can try to extract
 * record fields from.
 */
function getAtomDataTypeFields(
  type: Type,
  plugins: Plugins.PluginsType
): {
  [key: string]: Type;
} | null {
  const aliasType = Types.getAliasType(type, plugins);

  if (aliasType) {
    // handle the case where type is an alias of an alias
    return getAtomDataTypeFields(aliasType, plugins);
  }

  return Types.getRecordFields(type);
}

export const compressJSONNodes = weakMemo((plugins: Plugins.PluginsType) => {
  const defaultData = getIntegratedTypeDefaultValues(plugins);
  return (nodes: JSONNode[]) =>
    nodes.forEach((node) => {
      if (node.data) {
        // if the Atom DataType overrides the default values of integrated type,
        // for example by setting the search.exclude default value to `true`,
        // we prevent compression of the fields where the default values differ
        const nodeType = node.type;
        const atomDefaultData =
          nodeType && typeof nodeType === 'string'
            ? getAtomIntegratedTypeDefaultValues(plugins)(nodeType)
            : null;
        compress(node.data, defaultData, atomDefaultData);
      }
    });
});

const _exportElementAsJSONNode =
  (useLegacySerialization: boolean) =>
  (components: Component.ComponentMap) =>
  (element: ExpressionElement) => {
    if (isStringElement(element)) {
      return createTextJSONNodeFromElement(element);
    }

    const isBodyElement = isBody(element);

    const tag = getTag(element);

    const children = getChildren(element);

    const json: JSONNode = {} as any;

    json._id = element.id;
    json.type = getName(element.type);
    if (element.meta) {
      json.meta = element.meta;
    }

    json.tag = isBodyElement ? 'body' : tag;

    // If  we have literal children we don't want to pass those to the
    // `getElementDataAndBindForJSONNode` function
    const truePathsToOmit = children ? pathsToOmitWithChildren : pathsToOmit;
    //  temp and styleBlockIds do not need to be serialized at this point,
    // so we remove them from the element
    const cleanElement = truePathsToOmit.reduce((_element, pathToOmit) => {
      return Expressions.setElementData(
        _element,
        Expressions.deleteInRecord(
          Expressions.getElementData(_element),
          pathToOmit
        )
      );
    }, element);

    const isComponentInstance = isComponentInst(components)(element);
    const isUserComponentInstance = isUserComponentInst(components, element);
    const isPluginComponentInstance =
      isComponentInstance && !isUserComponentInstance;

    const {data: elementData, bind: bindData} =
      getElementDataAndBindForJSONNode(components)(
        isPluginComponentInstance ? element : cleanElement
      );

    if (!isPluginComponentInstance) {
      json.classes = getStyleBlockIds(element);
      json.children = children
        ? children.map<string>((child) => child.id)
        : emptyArray;
    } else {
      json.classes = emptyArray;
      json.children = emptyArray;
    }

    if (
      elementData &&
      isPlainObject(elementData) &&
      Object.keys(elementData).length
    ) {
      let cleanElementData = elementData;

      if (cleanElementData.value != null) {
        json.v = cleanElementData.value;
        cleanElementData = dissoc('value')(cleanElementData);
      }

      // These conditions are to fix the data location for Elements that have
      // their data stored in Literals instead of as Records
      if (cleanElementData.styleBlockIds) {
        json.classes = cleanElementData.styleBlockIds;
        cleanElementData = dissoc('styleBlockIds')(cleanElementData);
      }

      if (cleanElementData.temp) {
        cleanElementData = dissoc('temp')(cleanElementData);
      }

      if (Object.keys(cleanElementData).length > 0) {
        json.data = cleanElementData;
      }
    }

    if (bindData) {
      json.bind = bindData;
    }

    if (isComponentInstance) {
      json.data = {};

      if (elementData && elementData.sym) {
        if (elementData.sym.overrides) {
          const dataType = components.get(element.type)?.dataType;
          const recordFields =
            (dataType ? Types.getRecordFields(dataType) : emptyObject) ??
            emptyObject;

          elementData.sym.overrides = Object.entries(
            elementData.sym.overrides
          ).reduce<Record<string, any>>((overridesById, override) => {
            const [fieldId, overrideData] = override;

            if (Types.isListOfElements(recordFields[fieldId])) {
              overridesById[fieldId] = useLegacySerialization
                ? // @ts-expect-error - TS7006 - Parameter 'overrideData' implicitly has an 'any' type. | TS7006 - Parameter 'overrideNode' implicitly has an 'any' type.
                  overrideData.reduce<Array<any>>(
                    // @ts-expect-error - TS7006 - Parameter 'overrideNodes' implicitly has an 'any' type. | TS7006 - Parameter 'overrideNode' implicitly has an 'any' type.
                    (overrideNodes, overrideNode) => {
                      // Overrides can have children, and their children can have children, and.. etc
                      // This will export all of the children in the right order given the expression shape
                      const exportChildren = (
                        elementChild: ExpressionElement
                      ) => {
                        overrideNodes.push(
                          legacy_exportElementAsJSONNode(components)(
                            elementChild
                          )
                        );

                        const overrideChildren = getChildren(elementChild);
                        if (overrideChildren) {
                          overrideChildren.forEach(exportChildren);
                        }
                      };

                      exportChildren(overrideNode);

                      return overrideNodes;
                    },
                    []
                  )
                : // @ts-expect-error - TS18046 - 'overrideData' is of type 'unknown'.
                  overrideData.map<Array<string>>(
                    // @ts-expect-error - TS18046 - 'overrideNode' is of type 'unknown'.
                    (overrideNode) => overrideNode.id
                  );
            } else {
              overridesById[fieldId] = overrideData;
            }
            return overridesById;
          }, {});
        }
        json.data = elementData;
      }

      if (isUserComponentInstance) {
        json.type = 'Symbol';
      }
    }

    return json;
  };

export const exportElementAsJSONNode = _exportElementAsJSONNode(false);

// TEMP: we are temporarily exporting legacy serialization logic. Single-node sync
// expects the User Component instance JSON nodes to have the override JSON nodes
// in the `node.data.sym.overrides` so it cannot use the new serialization that
// flattens the overrides into arrays of node IDs yet.
// TODO Structure - Implement JsonDomUtils that can replace a flattened User Component
//   override subtree, and make the single-node sync backend using it. That will allow
//   us to get rid of this legacy serialization util.
export const legacy_exportElementAsJSONNode = _exportElementAsJSONNode(true);

export const exportElementAsJSONNodeArray = weakMemo(
  (components: Component.ComponentMap) =>
    weakMemo(
      (rootElement: ExpressionElement): Array<JSONNode> =>
        mapElementTreeToArray<JSONNode>(
          EElement(rootElement),
          components,
          exportElementAsJSONNode(components)
        )
    )
);

// TEMP: we are temporarily exporting legacy serialization logic. Single-node sync
// expects the User Component instance JSON nodes to have the override JSON nodes
// in the `node.data.sym.overrides` so it cannot use the new serialization that
// flattens the overrides into arrays of node IDs yet.
// TODO Structure - Implement JsonDomUtils that can replace a flattened User Component
//   override subtree, and make the single-node sync backend using it. That will allow
//   us to get rid of this legacy serialization util.
export const legacy_exportElementAsJSONNodeArray = weakMemo(
  (components: Component.ComponentMap) =>
    weakMemo(
      (rootElement: ExpressionElement): Array<JSONNode> =>
        legacySerializationMapElementTreeToArray<JSONNode>(
          rootElement,
          legacy_exportElementAsJSONNode(components)
        )
    )
);

const pageName = qualify(Plugins.name('__SitePlugin'), Component.name('page'));
const getBody = matchExpression({
  Element: (el) => (isBody(el) ? el : null),
  _: () => null,
});

export const exportElementsAsJSONNodesForPage = weakMemo(
  (components: Component.ComponentMap): Array<JSONNode> => {
    const page = components.get(pageName);

    if (!page) {
      return emptyArray;
    }

    const render = Component.getRender(page);

    const body = getBody(render);

    if (!body) {
      return emptyArray;
    }

    return mapElementTreeToArray<JSONNode>(render, components, (element) => {
      return exportElementAsJSONNode(components)(element);
    });
  }
);

export const exportSymbolAsJSONNodes = weakMemo(
  (components: Component.ComponentMap) =>
    weakMemo((symbolRoot: Component.ComponentType): Array<JSONNode> => {
      // Flow type refinement, this also gets checked upstream
      const render = Component.getRender(symbolRoot);
      const elementVal = Expressions.getElement(render);
      if (!elementVal) {
        return [];
      }

      const mapper = (element: ExpressionElement) => {
        const jsonNode = exportElementAsJSONNode(components)(element);
        const isRoot = isUserComponentRoot(components, element);

        if (isRoot) {
          if (!jsonNode.data) {
            jsonNode.data = {};
          }
          const {data} = jsonNode;
          if (!data.sym) {
            data.sym = {};
          }

          data.sym.root = true;
          data.sym.name = Component.getDisplayName(symbolRoot);
          const description = Component.getDescription(symbolRoot);
          if (description) {
            data.sym.description = description;
          }
          const groupName = Component.getGroupName(symbolRoot);
          if (groupName) {
            data.sym.groupName = groupName;
          }

          const origin = Component.getOrigin(symbolRoot);
          if (origin) {
            // We only set origin if it's defined
            data.sym.origin = origin;
          }
          const variants = Component.getVariants(symbolRoot);
          if (variants) {
            data.sym.variants = variants;
          }
          const source = Component.getSource(symbolRoot);
          if (source) {
            data.sym.source = source;
          }
          const dismissedPropElementIds =
            Component.getDismissedPropElementIds(symbolRoot);
          if (dismissedPropElementIds && dismissedPropElementIds.length > 0) {
            data.sym.dismissedPropElementIds = dismissedPropElementIds;
          }
          const placeholderBaseVariantName =
            Component.getPlaceholderBaseVariantName(symbolRoot);
          if (placeholderBaseVariantName) {
            data.sym.placeholderBaseVariantName = placeholderBaseVariantName;
          }
          if (Component.getHiddenForNonComponentManagers(symbolRoot)) {
            data.sym.hiddenForNonComponentManagers = true;
          }
          const defaultData = Component.getDefaultData(symbolRoot);
          if (defaultData) {
            data.sym.defaultData = stringifyExpression(defaultData);
          }

          // Read https://github.com/webflow/webflow/pull/39591 for context
          // as to why this is stringify'd
          jsonNode.dataType = JSON.stringify(Component.getDataType(symbolRoot));
        }

        return jsonNode;
      };

      return mapElementTreeToArray<JSONNode>(render, components, mapper);
    })
);

export const getSymbolRootsFromComponentMap = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo(
      (components: Component.ComponentMap): Array<Component.ComponentType> => {
        return components.reduce<Array<Component.ComponentType>>(
          (acc, component, name) => {
            if (namesEqual(name, pageName)) {
              return acc;
            }
            if (
              Expressions.isElement(Component.getRender(component)) &&
              !Plugins.isPluginComponent(plugins, component)
            ) {
              acc.push(component);
            }
            return acc;
          },
          []
        );
      }
    )
);

export const exportSymbolsAsJSONNodes = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((components: Component.ComponentMap): Array<JSONNode> => {
      const symbolRoots = getSymbolRootsFromComponentMap(plugins)(components);

      const jsonNodes = symbolRoots.reduce<Array<JSONNode>>(
        (acc, symbolRoot) => {
          const nodes = exportSymbolAsJSONNodes(components)(symbolRoot);
          acc.push(...nodes);
          return acc;
        },
        []
      );

      return jsonNodes;
    })
);

export const exportSymbolsAsJSONNodesById = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo(
      (components: Component.ComponentMap): Record<string, Array<JSONNode>> => {
        const symbolRoots = getSymbolRootsFromComponentMap(plugins)(components);

        return symbolRoots.reduce<Record<string, Array<JSONNode>>>(
          (acc, symbolRoot) => {
            const nodes = exportSymbolAsJSONNodes(components)(symbolRoot);
            const rootId = nodes.find((node) => node.data?.sym?.root)?._id;
            if (!rootId) {
              return acc;
            }
            acc[rootId] = nodes;
            return acc;
          },
          {}
        );
      }
    )
);

const uniqueElementIdMatcher = matchExpression({
  Element:
    (element: ExpressionElement, expression: Expression) =>
    (seenElementIds: Set<string>): [Set<string>, Expression] => {
      if (!element.id || seenElementIds.has(element.id)) {
        return pair(seenElementIds, EElement({...element, id: generateGuid()}));
      }
      seenElementIds.add(element.id);
      return pair(seenElementIds, expression);
    },
  _:
    (expression: Expression) =>
    (seenElementIds: Set<string>): [Set<string>, Expression] =>
      pair(seenElementIds, expression),
});

const deduplicateElementIds = (expression: Expression): Expression => {
  return Expressions.everywhereWithContext(uniqueElementIdMatcher)(
    new Set<string>(),
    expression
  );
};

function mapElementTreeToArray<V>(
  expr: Expression,
  componentMap: Component.ComponentMap,
  mapper: (arg1: ExpressionElement) => V
): Array<V> {
  return Expressions.everything(flip(concat))(
    matchExpression({
      Element: (el) => {
        return shouldStopWalking(el, componentMap)
          ? Expressions.stop([mapper(el)])
          : Expressions.resume([mapper(el)]);
      },
      _: constant(Expressions.resume([])),
    })
  )(deduplicateElementIds(expr));
}

function shouldStopWalking(
  element: ExpressionElement,
  componentMap: Component.ComponentMap
): boolean {
  const isCorruptUserComponentInst =
    isUserComponent(element.type) &&
    !isUserComponentInst(componentMap, element);

  if (isCorruptUserComponentInst) {
    // We have corrupt User Component instances that have children. To keep the
    // logic backward compatible, and to avoid data loss, we need to continue the
    // walk into the children even when the User Component does not exist in the
    // Component Map.
    const data = Expressions.getElementData(element);
    const children = Expressions.getIn(data, CHILDREN_PATH);

    return !children;
  }

  return false;
}

function legacySerializationMapElementTreeToArray<V>(
  element: ExpressionElement,
  mapper: (arg1: ExpressionElement) => V
): Array<V> {
  return reduceElementTree(
    element,
    (acc: Array<V>, childElement: ExpressionElement) => {
      acc.push(mapper(childElement));
      return acc;
    },
    []
  );
}
